import { useEffect, useState } from "react";
import { Plus, Edit, Trash2, Key, Lock, Unlock, BookOpen } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import AdminLayout from "@/components/layout/AdminLayout";

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [courses, setCourses] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [editing, setEditing] = useState(null);
  const [accessUser, setAccessUser] = useState(null);
  const [accessList, setAccessList] = useState([]);
  const [pwUser, setPwUser] = useState(null);
  const [newPw, setNewPw] = useState("");
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "", role: "aluno" });

  const load = () => api.get("/admin/users").then((r) => setUsers(r.data));
  useEffect(() => {
    document.title = "Usuárias — Admin";
    load();
    api.get("/admin/courses").then((r) => setCourses(r.data));
  }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/users", form);
      toast.success("Usuária criada");
      setShowNew(false);
      setForm({ name: "", email: "", phone: "", password: "", role: "aluno" });
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/admin/users/${editing.id}`, {
        name: editing.name, phone: editing.phone, status: editing.status, role: editing.role
      });
      toast.success("Atualizado");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const handleDelete = async (uid) => {
    if (!window.confirm("Excluir esta usuária? Esta ação não pode ser desfeita.")) return;
    try {
      await api.delete(`/admin/users/${uid}`);
      toast.success("Excluída");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const toggleStatus = async (u) => {
    const newStatus = u.status === "ativo" ? "inativo" : "ativo";
    await api.put(`/admin/users/${u.id}`, { status: newStatus });
    toast.success(`Conta ${newStatus}`);
    load();
  };

  const openAccess = async (u) => {
    setAccessUser(u);
    const { data } = await api.get(`/admin/users/${u.id}/access`);
    setAccessList(data);
  };

  const toggleCourseAccess = async (course_id) => {
    const has = accessList.some((a) => a.course_id === course_id);
    if (has) {
      await api.delete(`/admin/access?user_id=${accessUser.id}&course_id=${course_id}`);
    } else {
      await api.post(`/admin/access`, { user_id: accessUser.id, course_id });
    }
    const { data } = await api.get(`/admin/users/${accessUser.id}/access`);
    setAccessList(data);
    toast.success("Acesso atualizado");
  };

  const submitPwReset = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/admin/users/${pwUser.id}/reset-password`, { password: newPw });
      toast.success("Senha resetada");
      setPwUser(null); setNewPw("");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <AdminLayout>
      <div className="p-8 lg:p-12 max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-10">
          <div>
            <div className="eyebrow mb-2">Gerenciar</div>
            <h1 className="font-serif text-4xl" style={{ color: "var(--text-primary)" }}>Usuárias</h1>
          </div>
          <button onClick={() => setShowNew(true)} className="btn-primary" data-testid="new-user-btn">
            <Plus size={16} /> Nova usuária
          </button>
        </div>

        <div className="brand-card overflow-hidden">
          <table className="w-full text-sm">
            <thead style={{ background: "var(--bg)" }}>
              <tr className="text-left">
                <th className="p-4 font-medium" style={{ color: "var(--copper)" }}>Nome</th>
                <th className="p-4 font-medium" style={{ color: "var(--copper)" }}>E-mail</th>
                <th className="p-4 font-medium" style={{ color: "var(--copper)" }}>Tipo</th>
                <th className="p-4 font-medium" style={{ color: "var(--copper)" }}>Status</th>
                <th className="p-4 font-medium" style={{ color: "var(--copper)" }}>Cadastro</th>
                <th className="p-4"></th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} className="border-t" style={{ borderColor: "var(--border)" }} data-testid={`user-row-${u.email}`}>
                  <td className="p-4 font-medium" style={{ color: "var(--text-primary)" }}>{u.name}</td>
                  <td className="p-4" style={{ color: "var(--text-secondary)" }}>{u.email}</td>
                  <td className="p-4"><span className="text-xs uppercase tracking-wide px-2 py-1 rounded-full" style={{ background: u.role === "admin" ? "rgba(184,115,51,0.15)" : "var(--bg)", color: "var(--copper)" }}>{u.role}</span></td>
                  <td className="p-4">
                    <button onClick={() => toggleStatus(u)} className="text-xs uppercase tracking-wide px-2 py-1 rounded-full" style={{ background: u.status === "ativo" ? "rgba(34,197,94,0.12)" : "rgba(239,68,68,0.12)", color: u.status === "ativo" ? "#16a34a" : "#dc2626" }} data-testid={`user-status-${u.email}`}>{u.status}</button>
                  </td>
                  <td className="p-4 text-xs" style={{ color: "var(--text-secondary)" }}>{u.created_at?.slice(0, 10)}</td>
                  <td className="p-4">
                    <div className="flex gap-2 justify-end">
                      <button onClick={() => openAccess(u)} className="p-2 hover:bg-[color:var(--bg)] rounded-lg" title="Liberar cursos" data-testid={`access-btn-${u.email}`}>
                        <BookOpen size={16} style={{ color: "var(--copper)" }} />
                      </button>
                      <button onClick={() => setPwUser(u)} className="p-2 hover:bg-[color:var(--bg)] rounded-lg" title="Resetar senha" data-testid={`reset-pw-${u.email}`}>
                        <Key size={16} style={{ color: "var(--copper)" }} />
                      </button>
                      <button onClick={() => setEditing({ ...u })} className="p-2 hover:bg-[color:var(--bg)] rounded-lg" title="Editar" data-testid={`edit-user-${u.email}`}>
                        <Edit size={16} style={{ color: "var(--copper)" }} />
                      </button>
                      <button onClick={() => handleDelete(u.id)} className="p-2 hover:bg-[color:var(--bg)] rounded-lg" title="Excluir" data-testid={`delete-user-${u.email}`}>
                        <Trash2 size={16} style={{ color: "#dc2626" }} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* New user modal */}
        {showNew && (
          <Modal onClose={() => setShowNew(false)} title="Nova usuária">
            <form onSubmit={handleCreate} className="space-y-3">
              <input className="brand-input" placeholder="Nome" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              <input className="brand-input" type="email" placeholder="E-mail" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
              <input className="brand-input" placeholder="Telefone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
              <input className="brand-input" type="password" placeholder="Senha (mín. 6)" required minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
              <select className="brand-input" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>
                <option value="aluno">Aluno</option>
                <option value="admin">Admin</option>
              </select>
              <button type="submit" className="btn-primary w-full" data-testid="submit-new-user">Criar</button>
            </form>
          </Modal>
        )}

        {editing && (
          <Modal onClose={() => setEditing(null)} title="Editar usuária">
            <form onSubmit={handleUpdate} className="space-y-3">
              <input className="brand-input" required value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} />
              <input className="brand-input" placeholder="Telefone" value={editing.phone || ""} onChange={(e) => setEditing({ ...editing, phone: e.target.value })} />
              <select className="brand-input" value={editing.role} onChange={(e) => setEditing({ ...editing, role: e.target.value })}>
                <option value="aluno">Aluno</option>
                <option value="admin">Admin</option>
              </select>
              <select className="brand-input" value={editing.status} onChange={(e) => setEditing({ ...editing, status: e.target.value })}>
                <option value="ativo">Ativo</option>
                <option value="inativo">Inativo</option>
              </select>
              <button type="submit" className="btn-primary w-full">Salvar</button>
            </form>
          </Modal>
        )}

        {pwUser && (
          <Modal onClose={() => { setPwUser(null); setNewPw(""); }} title={`Nova senha para ${pwUser.name}`}>
            <form onSubmit={submitPwReset} className="space-y-3">
              <input className="brand-input" type="password" minLength={6} required placeholder="Nova senha (mín. 6)" value={newPw} onChange={(e) => setNewPw(e.target.value)} />
              <button type="submit" className="btn-primary w-full">Atualizar senha</button>
            </form>
          </Modal>
        )}

        {accessUser && (
          <Modal onClose={() => setAccessUser(null)} title={`Acessos de ${accessUser.name}`}>
            <p className="text-sm mb-3" style={{ color: "var(--text-secondary)" }}>Marque os cursos que esta usuária pode acessar.</p>
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {courses.map((c) => {
                const has = accessList.some((a) => a.course_id === c.id);
                return (
                  <div key={c.id} className="flex items-center justify-between p-3 rounded-lg" style={{ background: "var(--bg)" }}>
                    <div>
                      <div className="font-medium" style={{ color: "var(--text-primary)" }}>{c.title}</div>
                      {c.is_public && <div className="text-xs" style={{ color: "var(--copper)" }}>Aberto a todos</div>}
                    </div>
                    <button onClick={() => toggleCourseAccess(c.id)} className={has ? "btn-secondary" : "btn-primary"} style={{ padding: "0.4rem 1rem", fontSize: "0.75rem" }} data-testid={`toggle-access-${c.id}`}>
                      {has ? <><Unlock size={12} /> Liberado</> : <><Lock size={12} /> Liberar</>}
                    </button>
                  </div>
                );
              })}
            </div>
          </Modal>
        )}
      </div>
    </AdminLayout>
  );
}

function Modal({ children, title, onClose }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-6" onClick={onClose}>
      <div className="bg-white rounded-2xl p-8 max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <h3 className="font-serif text-2xl mb-5" style={{ color: "var(--text-primary)" }}>{title}</h3>
        {children}
      </div>
    </div>
  );
}
